/*

  Audio.ts

  Audio management service for Netskrafl/Explo

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { GlobalState } from "./globalstate";
import { serverUrl } from "./requestWithoutAuth";

// Sound identifiers used in the application
export type SoundId = "your-turn" | "you-win" | "new-msg";

// Map of sound IDs to their URLs
export type SoundUrlMap = Record<SoundId, string>;

/**
 * AudioManager handles preloading and playback of sound effects.
 * It creates HTMLAudioElement instances for each sound and manages their lifecycle.
 */
class AudioManager {
  private sounds: Map<SoundId, HTMLAudioElement> = new Map();
  private soundUrls: SoundUrlMap;
  private initialized: boolean = false;

  constructor(state: GlobalState, soundUrls?: Partial<SoundUrlMap>) {
    // By default, sound URLs are based on /static on the backend server
    const DEFAULT_SOUND_BASE = serverUrl(state, "/static");

    const DEFAULT_SOUND_URLS: SoundUrlMap = {
      "your-turn": `${DEFAULT_SOUND_BASE}/your-turn.mp3`,
      "you-win": `${DEFAULT_SOUND_BASE}/you-win.mp3`,
      "new-msg": `${DEFAULT_SOUND_BASE}/new-msg.mp3`,
    };

    // Merge provided URLs with defaults
    this.soundUrls = {
      ...DEFAULT_SOUND_URLS,
      ...(soundUrls || {}),
    };
  }

  /**
   * Initialize the audio manager by creating and preloading audio elements.
   * This should be called once when the application starts.
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }

    // Create audio elements for each sound
    Object.entries(this.soundUrls).forEach(([soundId, url]) => {
      const audio = new Audio(url);
      audio.preload = "auto";

      // Handle load errors gracefully - don't let them crash the app
      audio.addEventListener("error", () => {
        console.warn(`Failed to load audio: ${soundId} from ${url}`);
      });

      this.sounds.set(soundId as SoundId, audio);
    });

    this.initialized = true;
  }

  /**
   * Play a sound by its ID.
   * If the sound is not loaded or fails to play, the error is logged but doesn't throw.
   *
   * @param soundId The identifier of the sound to play
   */
  play(soundId: SoundId): void {
    if (!this.initialized) {
      this.initialize();
    }

    const audio = this.sounds.get(soundId);
    if (!audio) {
      console.warn(`Audio not found: ${soundId}`);
      return;
    }

    // Reset to start in case it's already playing
    audio.currentTime = 0;

    // Play the audio - catch any errors (e.g., user hasn't interacted with page yet)
    audio.play().catch((err) => {
      // This is expected in some cases (e.g., autoplay restrictions)
      // so we just log it at debug level
      if (err.name !== "NotAllowedError") {
        console.warn(`Failed to play audio ${soundId}:`, err);
      }
    });
  }

  /**
   * Update the URL for a specific sound.
   * This will recreate the audio element with the new URL.
   *
   * @param soundId The identifier of the sound to update
   * @param url The new URL for the sound
   */
  updateSoundUrl(soundId: SoundId, url: string): void {
    this.soundUrls[soundId] = url;

    // If already initialized, recreate this audio element
    if (this.initialized) {
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.addEventListener("error", () => {
        console.warn(`Failed to load audio: ${soundId} from ${url}`);
      });
      this.sounds.set(soundId, audio);
    }
  }

  /**
   * Dispose of all audio elements and clean up resources.
   */
  dispose(): void {
    this.sounds.forEach((audio) => {
      audio.pause();
      audio.src = "";
    });
    this.sounds.clear();
    this.initialized = false;
  }
}

// Global singleton instance
let audioManager: AudioManager | null = null;

/**
 * Get or create the global AudioManager instance.
 *
 * @param soundUrls Optional custom sound URLs (only used on first call)
 * @returns The global AudioManager instance
 */
export function getAudioManager(state: GlobalState, soundUrls?: Partial<SoundUrlMap>): AudioManager {
  if (!audioManager) {
    audioManager = new AudioManager(state, soundUrls);
  }
  return audioManager;
}

/**
 * Reset the global AudioManager instance.
 * Useful for testing or when changing sound URLs.
 */
export function resetAudioManager(): void {
  if (audioManager) {
    audioManager.dispose();
    audioManager = null;
  }
}
